import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SharedProps } from '@/types'
import { AlertCircleIcon, SaveIcon, ArrowLeftIcon } from 'lucide-react'

interface Props {
  category: {
    id: number | null
    name: string
    kind: string
  }
}

export default function Form({ category }: Props) {
  const { errors } = usePage<SharedProps>().props
  const isEditing = category.id !== null

  const { data, setData, post, put, processing } = useForm({
    name: category.name,
    kind: category.kind,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isEditing) {
      put(`/categories/${category.id}`)
    } else {
      post('/categories')
    }
  }

  return (
    <>
      <Head title={isEditing ? `Edit ${category.name}` : 'New Category'} />
      <PageHeader
        title={isEditing ? 'Edit Category' : 'New Category'}
        description={isEditing ? `Editing ${category.name}` : 'Create a new category for your transactions'}
      >
        <Link href="/categories">
          <Button variant="outline" size="sm" className="gap-1.5">
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Back
          </Button>
        </Link>
      </PageHeader>

      <PageContent>
        <div className="max-w-lg">
          {errors && Object.keys(errors).length > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 mb-5">
              <AlertCircleIcon className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <ul className="text-[13px] text-destructive list-disc pl-4 space-y-1">
                {Object.values(errors).flat().map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-[15px]">Category Details</CardTitle>
              <CardDescription className="text-[13px]">
                Categories help you organize and track your transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-[13px] font-medium">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    placeholder="e.g., Office Supplies"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="kind" className="text-[13px] font-medium">
                    Type
                  </Label>
                  <Select value={data.kind} onValueChange={(v) => setData('kind', v)}>
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-success" />
                          Revenue
                        </span>
                      </SelectItem>
                      <SelectItem value="cost">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-destructive" />
                          Cost
                        </span>
                      </SelectItem>
                      <SelectItem value="transfer">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-primary" />
                          Transfer
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-3">
                  <Button type="submit" disabled={processing} size="sm" className="gap-1.5">
                    <SaveIcon className="h-3.5 w-3.5" />
                    {isEditing ? 'Update Category' : 'Create Category'}
                  </Button>
                  <Link href="/categories">
                    <Button variant="outline" size="sm" type="button">
                      Cancel
                    </Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </>
  )
}
